// Opstrait — Sprint 1 Week 2 — Item W2.1
// Minimal SSE consumer for POST endpoints. Uses fetch + ReadableStream because
// EventSource only supports GET. Parses standard SSE frames:
//   event: <name>\ndata: <json or text>\n\n
// Yields { event, data } to a per-frame callback.

export async function streamSSE({ url, method = "POST", headers = {}, body, onEvent, onError }) {
  const finalHeaders = {
    "Content-Type": "application/json",
    "Accept": "text/event-stream",
    ...headers,
  };
  const ctrl = new AbortController();
  try {
    const res = await fetch(url, {
      method,
      headers: finalHeaders,
      body: body == null ? undefined : (typeof body === "string" ? body : JSON.stringify(body)),
      signal: ctrl.signal,
    });
    if (!res.ok || !res.body) {
      let errPayload = null;
      try { errPayload = await res.json(); } catch (_) { /* ignore */ }
      onError && onError({
        status: res.status,
        retryAfter: res.headers.get("Retry-After"),
        message: errPayload?.detail || `HTTP ${res.status}`,
        payload: errPayload,
      });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";
    // SSE frames are separated by blank lines (\n\n).
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf("\n\n")) >= 0) {
        const rawFrame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const lines = rawFrame.split("\n");
        let event = "message";
        const dataLines = [];
        for (const ln of lines) {
          if (ln.startsWith("event:")) event = ln.slice(6).trim();
          else if (ln.startsWith("data:")) dataLines.push(ln.slice(5).trim());
        }
        const dataStr = dataLines.join("\n");
        let parsed = dataStr;
        try { parsed = JSON.parse(dataStr); } catch (_) { /* leave as string */ }
        try { onEvent && onEvent({ event, data: parsed }); } catch (_) { /* ignore consumer errors */ }
      }
    }
  } catch (e) {
    if (e.name !== "AbortError") onError && onError({ status: 0, message: e.message || String(e) });
  }
  return () => ctrl.abort();
}
